// Voice editor — 5. Notifications section. Extracted verbatim from
// `VoiceEditorPage.tsx` (no behavior change).

import { useEffect, useState } from 'react';

import {
  type NotificationChannelCapabilities,
  type VoiceAccountSummary,
  type VoiceRow,
} from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { Banner, Button, FieldAssembly, Input, Segmented, Select, Switch } from '@barel/ui';

import { SectionShell, type SectionPropsWithVoice } from './SectionShell';

interface NotificationsSectionProps extends SectionPropsWithVoice {
  account: VoiceAccountSummary;
  notificationChannels: NotificationChannelCapabilities;
  onSendTest: (destination: string) => void;
  testBusy: boolean;
}

export function NotificationsSection({
  voice,
  flush,
  account,
  notificationChannels,
  onSendTest,
  testBusy,
}: NotificationsSectionProps) {
  const t = useT('voice');

  const channelValue =
    voice.notifyChannel === 'whatsapp'
      ? (voice.notifyWhatsapp ?? '')
      : voice.notifyChannel === 'telegram'
        ? (voice.notifyTelegram ?? '')
        : voice.notifyChannel === 'slack'
          ? (voice.notifySlack ?? '')
          : (voice.notifyEmail ?? '');

  const valuePlaceholder =
    voice.notifyChannel === 'whatsapp'
      ? t('notifications.valueWhatsappPlaceholder')
      : voice.notifyChannel === 'telegram'
        ? t('notifications.valueTelegramPlaceholder')
        : voice.notifyChannel === 'slack'
          ? t('notifications.valueSlackPlaceholder')
          : t('notifications.valueEmailPlaceholder');

  const handleValueBlur = (raw: string): void => {
    if (voice.notifyChannel === 'whatsapp') {
      if (raw !== (voice.notifyWhatsapp ?? '')) void flush({ notifyWhatsapp: raw });
    } else if (voice.notifyChannel === 'telegram') {
      if (raw !== (voice.notifyTelegram ?? '')) void flush({ notifyTelegram: raw });
    } else if (voice.notifyChannel === 'slack') {
      if (raw !== (voice.notifySlack ?? '')) void flush({ notifySlack: raw });
    } else if (voice.notifyChannel === 'email') {
      if (raw !== (voice.notifyEmail ?? '')) void flush({ notifyEmail: raw });
    }
  };

  const selectedCapability = notificationChannels[voice.notifyChannel];
  const hotLeadEnabled = selectedCapability.hotLeadAvailable && voice.notifyHotEnabled;
  const reviewEnabled = selectedCapability.reviewAvailable && voice.notifyReviewEnabled;
  const recapEnabled = selectedCapability.dailyRecapAvailable && voice.notifyRecapEnabled;
  const triggersOn = hotLeadEnabled || reviewEnabled;
  const unavailableBodyKey =
    selectedCapability.reason === 'slack_bot_token_not_configured'
      ? 'notifications.slackUnavailableBody'
      : selectedCapability.reason === 'whatsapp_sender_id_not_configured'
        ? 'notifications.whatsappUnavailableBody'
        : 'notifications.providerUnavailableBody';
  const previewVisible = triggersOn && channelValue.length > 0 && selectedCapability.available;

  return (
    <SectionShell
      id="voice-notifications"
      title={t('notifications.title')}
      subtitle={t('notifications.subtitle')}
    >
      <div className="flex flex-col gap-4">
        <FieldAssembly
          label={t('notifications.channelLabel')}
          description={t('notifications.channelSub')}
          asChild
        >
          <div role="group" aria-label={t('notifications.channelLabel')}>
            <Segmented<'whatsapp' | 'telegram' | 'slack' | 'email'>
              value={voice.notifyChannel}
              onValueChange={(next) => {
                void flush({ notifyChannel: next });
              }}
              options={[
                {
                  value: 'whatsapp',
                  label: notificationChannels.whatsapp.available
                    ? t('notifications.channelWhatsapp')
                    : `${t('notifications.channelWhatsapp')} · ${t('notifications.unavailableShort')}`,
                  disabled: !notificationChannels.whatsapp.available,
                },
                {
                  value: 'telegram',
                  label: notificationChannels.telegram.available
                    ? t('notifications.channelTelegram')
                    : `${t('notifications.channelTelegram')} · ${t('notifications.unavailableShort')}`,
                  disabled: !notificationChannels.telegram.available,
                },
                {
                  value: 'slack',
                  label: notificationChannels.slack.available
                    ? t('notifications.channelSlack')
                    : `${t('notifications.channelSlack')} · ${t('notifications.unavailableShort')}`,
                  disabled: !notificationChannels.slack.available,
                },
                {
                  value: 'email',
                  label: notificationChannels.email.available
                    ? t('notifications.channelEmail')
                    : `${t('notifications.channelEmail')} · ${t('notifications.unavailableShort')}`,
                  disabled: !notificationChannels.email.available,
                },
              ]}
              aria-label={t('notifications.channelLabel')}
              size="sm"
            />
          </div>
        </FieldAssembly>

        {!selectedCapability.available ? (
          <Banner
            tone="warning"
            title={t('notifications.channelUnavailableTitle')}
            data-testid="voice-notif-channel-unavailable"
          >
            {t(unavailableBodyKey)}
          </Banner>
        ) : null}

        <ChannelValueRow
          channel={voice.notifyChannel}
          value={channelValue}
          placeholder={valuePlaceholder}
          onBlur={handleValueBlur}
          onSendTest={onSendTest}
          testBusy={testBusy}
          channelAvailable={selectedCapability.available}
        />

        {previewVisible ? (
          <div
            className="rounded-xl border border-default bg-bg-elevated px-4 py-3"
            data-testid="voice-notif-preview"
          >
            <div className="text-2xs font-semibold uppercase tracking-wider text-secondary">
              {t('notifications.previewTitle')}
            </div>
            <p className="mt-1 text-sm text-primary">
              {voice.notifyHotEnabled
                ? t('notifications.previewHotLead')
                : t('notifications.previewReviewPending')}
            </p>
          </div>
        ) : null}

        <div className="flex flex-col gap-3">
          <h3 className="text-2xs font-semibold uppercase tracking-wider text-secondary">
            {t('notifications.triggersTitle')}
          </h3>
          <FieldAssembly
            label={t('notifications.triggerHotLabel')}
            description={
              selectedCapability.hotLeadAvailable
                ? t('notifications.triggerHotSub')
                : t('notifications.triggerHotUnavailableSub')
            }
            asChild
          >
            <Switch
              aria-label={t('notifications.triggerHotLabel')}
              checked={hotLeadEnabled}
              disabled={!selectedCapability.hotLeadAvailable}
              onCheckedChange={(next: boolean) => {
                void flush({ notifyHotEnabled: next });
              }}
              data-testid="voice-notif-hot-switch"
            />
          </FieldAssembly>
          {hotLeadEnabled && account.aiMode === 'auto' ? (
            <FieldAssembly label={t('notifications.triggerHotLabel')} asChild>
              <div role="group" aria-label={t('notifications.triggerHotLabel')}>
                <Segmented<'auto' | 'validate'>
                  value={voice.notifyHotAction === 'validate' ? 'validate' : 'auto'}
                  onValueChange={(next) => {
                    void flush({ notifyHotAction: next });
                  }}
                  options={[
                    {
                      value: 'auto',
                      label: t('notifications.triggerHotActionAuto'),
                    },
                    {
                      value: 'validate',
                      label: t('notifications.triggerHotActionValidate'),
                    },
                  ]}
                  aria-label={t('notifications.triggerHotLabel')}
                  size="sm"
                />
              </div>
            </FieldAssembly>
          ) : null}

          {account.aiMode === 'review' ? (
            <FieldAssembly
              label={t('notifications.triggerReviewLabel')}
              description={
                selectedCapability.reviewAvailable
                  ? t('notifications.triggerReviewSub')
                  : t('notifications.triggerReviewUnavailableSub')
              }
              asChild
            >
              <Switch
                aria-label={t('notifications.triggerReviewLabel')}
                checked={reviewEnabled}
                disabled={!selectedCapability.reviewAvailable}
                onCheckedChange={(next: boolean) => {
                  void flush({ notifyReviewEnabled: next });
                }}
                data-testid="voice-notif-review-switch"
              />
            </FieldAssembly>
          ) : null}

          <FieldAssembly
            label={t('notifications.triggerRecapLabel')}
            description={t('notifications.triggerRecapSub')}
            asChild
          >
            <Switch
              aria-label={t('notifications.triggerRecapLabel')}
              checked={recapEnabled}
              disabled={!selectedCapability.dailyRecapAvailable}
              onCheckedChange={(next: boolean) => {
                void flush({ notifyRecapEnabled: next });
              }}
              data-testid="voice-notif-recap-switch"
            />
          </FieldAssembly>
          {recapEnabled && selectedCapability.recapHourConfigurable ? (
            <FieldAssembly label={t('notifications.triggerRecapHourLabel')} asChild>
              <Select
                value={voice.notifyRecapHour}
                onValueChange={(next: string) => {
                  void flush({ notifyRecapHour: next });
                }}
              >
                <Select.Trigger
                  aria-label={t('notifications.triggerRecapHourLabel')}
                  data-testid="voice-notif-recap-hour"
                >
                  <Select.Value />
                </Select.Trigger>
                <Select.Portal>
                  <Select.Content>
                    {recapHours().map((hour) => (
                      <Select.Item key={hour} value={hour}>
                        {hour}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Portal>
              </Select>
            </FieldAssembly>
          ) : null}

          <FieldAssembly
            label={t('notifications.replyLabel')}
            description={
              selectedCapability.repliesAvailable
                ? t('notifications.replySub')
                : t('notifications.replyUnavailableSub')
            }
            asChild
          >
            <Switch
              aria-label={t('notifications.replyLabel')}
              checked={selectedCapability.repliesAvailable && voice.notifyReplyEnabled}
              disabled={!selectedCapability.repliesAvailable}
              onCheckedChange={(next: boolean) => {
                void flush({ notifyReplyEnabled: next });
              }}
              data-testid="voice-notif-reply-switch"
            />
          </FieldAssembly>
        </div>
      </div>
    </SectionShell>
  );
}

interface ChannelValueRowProps {
  channel: VoiceRow['notifyChannel'];
  value: string;
  placeholder: string;
  onBlur: (raw: string) => void;
  onSendTest: (destination: string) => void;
  testBusy: boolean;
  channelAvailable: boolean;
}

function ChannelValueRow({
  channel,
  value,
  placeholder,
  onBlur,
  onSendTest,
  testBusy,
  channelAvailable,
}: ChannelValueRowProps) {
  const t = useT('voice');
  const [draft, setDraft] = useState(value);
  // Re-prime when the underlying value changes (channel switch).
  useEffect(() => {
    setDraft(value);
  }, [value, channel]);

  return (
    <div className="flex items-end gap-2">
      <div className="flex-1">
        <FieldAssembly label={t('notifications.channelLabel')}>
          <Input
            type="text"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => onBlur(draft.trim())}
            placeholder={placeholder}
            data-testid="voice-notif-value"
          />
        </FieldAssembly>
      </div>
      <Button
        variant="secondary"
        size="md"
        type="button"
        onClick={() => onSendTest(draft.trim())}
        disabled={!draft.trim() || testBusy || !channelAvailable}
        loading={testBusy}
        data-testid="voice-notif-test"
      >
        {t('notifications.testCta')}
      </Button>
    </div>
  );
}

function recapHours(): string[] {
  const out: string[] = [];
  for (let h = 0; h < 24; h++) {
    out.push(`${h.toString().padStart(2, '0')}:00`);
  }
  return out;
}
